<template>
  <v-dialog v-model="openMode" max-width="800px" persistent content-class="import-dialog">
    <v-card class="import-card">
      <!-- Dialog Header -->
      <div class="import-header">
        <h2 class="import-title">{{ currentStep === 'upload' ? 'ייבוא אנשי קשר מאקסל' : 'תוצאות ייבוא' }}</h2>
        <p class="import-subtitle" v-if="currentStep === 'upload'">העלה קובץ אקסל או הורד תבנית לדוגמה</p>
        <p class="import-subtitle" v-else>סיכום תוצאות הייבוא</p>
      </div>

      <v-divider class="mx-4"></v-divider>

      <!-- Step 1: Upload File -->
      <div v-if="currentStep === 'upload'" class="import-content">
        <div class="import-options">
          <!-- Upload Option -->
          <div class="import-option">
            <div class="upload-area" :class="{ 'drag-over': isDragging }" @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false" @drop.prevent="handleFileDrop" @click="$refs.fileInput.click()">
              <input type="file" ref="fileInput" style="display: none;" accept=".xls, .xlsx" @change="handleFileUpload">

              <div class="upload-icon-container">
                <v-icon size="48" color="primary" class="upload-icon">
                  {{ isDragging ? 'mdi-file-upload' : 'mdi-file-upload-outline' }}
                </v-icon>
                <div class="upload-pulse" v-if="isDragging"></div>
              </div>

              <h3 class="upload-title">העלאת קובץ אקסל</h3>
              <p class="upload-text">גרור ושחרר קובץ כאן או לחץ לבחירת קובץ</p>
              <p class="upload-format">קבצים נתמכים: .xlsx, .xls</p>
            </div>

            <v-btn color="primary" block class="mt-4 upload-btn" elevation="1" @click="$refs.fileInput.click()">
              <v-icon left>mdi-file-upload</v-icon>
              בחר קובץ
            </v-btn>
          </div>

          <!-- Divider -->
          <div class="option-divider">
            <div class="divider-line"></div>
            <div class="divider-text">או</div>
            <div class="divider-line"></div>
          </div>

          <!-- Template Option -->
          <div class="import-option">
            <div class="template-area" @click="downloadExampleExcel()">
              <div class="template-icon-container">
                <v-icon size="48" color="secondary" class="template-icon">mdi-file-download-outline</v-icon>
              </div>

              <h3 class="template-title">הורדת תבנית</h3>
              <p class="template-text">הורד קובץ אקסל לדוגמה למילוי</p>

              <div class="template-preview">
                <div class="template-header">
                  <div class="template-cell">שם</div>
                  <div class="template-cell">טלפון</div>
                  <div class="template-cell">קוד מדינה</div>
                  <div class="template-cell">הרשאה</div>
                  <!-- <div class="template-cell">אישר קבלת צינתוק</div> -->

                </div>
                <div class="template-row">
                  <div class="template-cell">ישראל ישראלי</div>
                  <div class="template-cell">0501234567</div>
                  <div class="template-cell">IL</div>
                  <div class="template-cell">user</div>
                </div>
              </div>
            </div>

            <v-btn color="secondary" block class="mt-4 template-btn" elevation="1" @click="downloadExampleExcel()">
              <v-icon left>mdi-file-download</v-icon>
              הורד תבנית
            </v-btn>
          </div>
        </div>

        <!-- Selected File Info -->
        <v-slide-y-transition>
          <div class="selected-file" v-if="selectedFile">
            <div class="file-info">
              <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
              <div class="file-details">
                <div class="file-name">{{ selectedFile.name }}</div>
                <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
              </div>
            </div>

            <div class="file-actions">
              <v-btn text color="error" @click="selectedFile = null">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-slide-y-transition>
      </div>

      <!-- Step 2: Import Results -->
      <div v-else-if="currentStep === 'results'" class="results-content">
        <div class="results-summary">
          <div class="result-cards">
            <!-- Success Card -->
            <v-card class="result-card success-card" elevation="2">
              <div class="result-icon-wrapper success-bg ml-2">
                <v-icon size="32" color="white">mdi-check-circle</v-icon>
              </div>
              <div class="result-details">
                <div class="result-count">{{ importedSuccess.length }}</div>
                <div class="result-label">אנשי קשר נוספו בהצלחה</div>
              </div>
            </v-card>

            <!-- Failed Card -->
            <v-card class="result-card error-card" elevation="2">
              <div class="result-icon-wrapper error-bg ml-2">
                <v-icon size="32" color="white">mdi-alert-circle</v-icon>
              </div>
              <div class="result-details">
                <div class="result-count">{{ importedFailed.length }}</div>
                <div class="result-label">אנשי קשר נכשלו</div>
              </div>
            </v-card>
          </div>

          <!-- Failed Items List (if any) -->
          <v-expand-transition>
            <div v-if="importedFailed.length > 0" class="failed-items-container">
              <div class="failed-items-header" @click="showFailedItems = !showFailedItems">
                <div class="failed-items-title">
                  <v-icon small color="error" class="ml-1">mdi-alert-circle</v-icon>
                  פירוט שגיאות
                </div>
                <v-btn icon small>
                  <v-icon>{{ showFailedItems ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </div>

              <v-expand-transition>
                <div v-if="showFailedItems" class="failed-items-list">
                  <div v-for="(item, index) in importedFailed" :key="index" class="failed-item">
                    <div class="failed-item-phone">{{ item.phone || 'מספר לא תקין' }}</div>
                    <div class="failed-item-reason">{{ item.reason || 'שגיאה לא ידועה' }}</div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </v-expand-transition>

          <!-- Send Call Option -->
          <div class="send-call-option">
            <v-checkbox v-model="sendCall" hide-details color="primary" class="send-call-checkbox">
              <template v-slot:label>
                <span class="checkbox-label">שלח צינתוק לאנשי הקשר החדשים</span>
              </template>
            </v-checkbox>
          </div>
        </div>
      </div>

      <!-- Loading Progress -->
      <v-slide-y-transition>
        <div class="progress-container" v-if="progressShow">
          <v-progress-linear indeterminate color="primary" height="4" class="mb-3"></v-progress-linear>
          <div class="progress-text">{{ progressText }}</div>
        </div>
      </v-slide-y-transition>

      <!-- Dialog Actions -->
      <v-card-actions class="import-actions">
        <v-spacer></v-spacer>

        <!-- Step 1 Actions -->
        <template v-if="currentStep === 'upload'">
          <v-btn text color="grey darken-1" @click="closeDialog" :disabled="progressShow">
            ביטול
          </v-btn>
          <v-btn color="primary" :disabled="!selectedFile || progressShow" :loading="progressShow" @click="processFile"
            class="import-btn">
            <v-icon left>mdi-database-import</v-icon>
            המשך
          </v-btn>
        </template>

        <!-- Step 2 Actions -->
        <template v-else-if="currentStep === 'results'">
          <v-btn text color="grey darken-1" @click="cancelImport" :disabled="progressShow">
            בטל ייבוא
          </v-btn>
          <v-btn color="primary" :loading="progressShow" :disabled="progressShow" @click="saveImported"
            class="import-btn">
            <v-icon left>mdi-content-save</v-icon>
            שמור נתונים
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </v-dialog>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
  props: {
    value: { type: Boolean, default: false },
  },
  components: {
    SnackBar,
  },
  data: () => ({
    currentStep: 'upload', // 'upload' or 'results'
    progressShow: false,
    progressText: 'מעבד את הקובץ...',
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    isDragging: false,
    selectedFile: null,
    importedSuccess: [],
    importedFailed: [],
    sendCall: false,
    showFailedItems: false
  }),

  methods: {
    // File handling methods
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    validateAndSetFile(file) {
      // Check if file is Excel
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
      ];

      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidExtension = ['xls', 'xlsx'].includes(fileExtension);

      if (validTypes.includes(file.type) || isValidExtension) {
        this.selectedFile = file;
      } else {
        this.showSnackBar('אנא בחר קובץ אקסל תקין (.xls או .xlsx)', 'red');
      }
    },

    async downloadExampleExcel() {
      this.$emit("exportExampleExcel");
    },

    // Process the selected file
    processFile() {
      if (!this.selectedFile) return;

      this.progressShow = true;
      this.progressText = 'מעבד את הקובץ...';

      // Reset arrays for new import
      this.importedSuccess = [];
      this.importedFailed = [];

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Process each row
          jsonData.forEach(row => {
            let { name, phone, callerid_country_code, recieve_calls, role } = row;

            // Skip empty rows
            if (!phone && !name) {
              return;
            }

            // Validate phone number
            if (!phone) {
              this.importedFailed.push({ phone: 'חסר', reason: 'מספר טלפון חסר' });
              return;
            }

            // Convert to string and format
            phone = String(phone);

            // Add leading zero if missing
            if (!phone.startsWith("0")) {
              phone = "0" + phone;
            }

            // Validate phone length
            if (phone.length < 9) {
              this.importedFailed.push({ phone, reason: 'מספר טלפון קצר מדי' });
              return;
            }

            // Validate phone format (numbers only)
            const numberRegex = /^\d+$/;
            if (!numberRegex.test(phone)) {
              this.importedFailed.push({ phone, reason: 'מספר טלפון מכיל תווים לא חוקיים' });
              return;
            }

            // Check for duplicates in current import
            const isDuplicateInImport = this.importedSuccess.some(item => item.phone === phone);
            if (isDuplicateInImport) {
              this.importedFailed.push({ phone, reason: 'מספר טלפון מופיע פעמיים בקובץ' });
              return;
            }

            if (!role || role === 'owner') {
              role = 'user';
            }

            // Emit event to check for duplicates in existing data
            this.$emit('checkDuplicate', phone, (isDuplicate) => {
              if (isDuplicate) {
                this.importedFailed.push({ phone, reason: 'מספר טלפון כבר קיים במערכת' });
              } else {
                // Add to success list
                this.importedSuccess.push({
                  name: name || 'ללא שם',
                  phone,
                  role,
                  callerid_country_code: callerid_country_code || 'IL',
                  recieve_calls: recieve_calls || 'לא'
                });
              }
            });

            // Add leading zero if missing
            if (recieve_calls && recieve_calls !== "כן" && recieve_calls !== "לא") {
              this.importedFailed.push({ phone, reason: 'אישור צינתוקים לא חוקי' });
              return;
            }
          });



          // Move to results step
          setTimeout(() => {
            this.progressShow = false;
            this.currentStep = 'results';
          }, 800);

        } catch (error) {
          console.error('Error parsing Excel file:', error);
          this.showSnackBar('שגיאה בקריאת קובץ האקסל: ' + error.message, 'red');
          this.progressShow = false;
        }
      };

      reader.onerror = (e) => {
        this.showSnackBar('שגיאה בקריאת הקובץ: ' + e.target.error, 'red');
        this.progressShow = false;
      };

      reader.readAsArrayBuffer(this.selectedFile);
    },

    // Save imported data
    async saveImported() {
      if (this.importedSuccess.length === 0) {
        this.showSnackBar('אין נתונים לשמירה', 'red');
        return;
      }

      this.progressShow = true;
      this.progressText = 'שומר נתונים...';

      const adminToken = localStorage.getItem("tokenAdmin");
      const filteredCodesJSON = JSON.stringify({
        subscribers: this.importedSuccess,
        sendCall: this.sendCall,
        adminToken: adminToken ? adminToken : false
      });

      let token = localStorage.getItem("token");

      try {
        let api = process.env.VUE_APP_BASE_URL + "/subscribes/create_subscribes_imported";
        const res = await fetch(api, ApiServices.requestOptions("POST", filteredCodesJSON, token));

        const jsonObject = await res.json();

        if (res.status === 200) {
          this.$emit("importedSuccessAdded");
          this.closeDialog();
        } else {
          this.showSnackBar('שגיאה: ' + jsonObject, "red");
        }
      } catch (error) {
        this.showSnackBar("Error saved subscribes: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    // Cancel import
    cancelImport() {
      this.closeDialog();
    },

    // Close dialog and reset state
    closeDialog() {
      this.resetState();
      this.openMode = false;
    },

    // Reset all state variables
    resetState() {
      this.currentStep = 'upload';
      this.selectedFile = null;
      this.importedSuccess = [];
      this.importedFailed = [];
      this.sendCall = false;
      this.progressShow = false;
      this.isDragging = false;
      this.showFailedItems = false;
    },

    // Utility methods
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    }
  },
  computed: {
    openMode: {
      get() {
        return this.value;
      },
      set(v) {
        return this.$emit('input', v);
      }
    },
  },
  watch: {
    openMode(val) {
      if (!val) {
        // Reset state when dialog closes
        this.resetState();
      }
    }
  }
}
</script>

<style scoped>
.import-dialog {
  border-radius: 16px;
}

.import-card {
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(to bottom, #ffffff, #f8f9fa);
}

.import-header {
  padding: 24px 24px 16px;
  text-align: center;
}

.import-title {
  color: #0d2c6d;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.import-subtitle {
  color: #64748b;
  font-size: 16px;
}

/* Upload Step Styles */
.import-content {
  padding: 24px;
}

.import-options {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.import-option {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.option-divider {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
}

.divider-line {
  width: 1px;
  height: 80px;
  background-color: #e2e8f0;
}

.divider-text {
  margin: 16px 0;
  color: #64748b;
  font-weight: 500;
}

/* Upload Area */
.upload-area {
  height: 280px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8fafc;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #0d2c6d;
  background-color: #f1f5f9;
}

.drag-over {
  border-color: #0d2c6d;
  background-color: rgba(13, 44, 109, 0.05);
  transform: scale(1.01);
}

.upload-icon-container {
  position: relative;
  margin-bottom: 16px;
}

.upload-icon {
  transition: transform 0.3s ease;
}

.drag-over .upload-icon {
  transform: scale(1.2);
}

.upload-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: rgba(13, 44, 109, 0.1);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }

  70% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0;
  }

  100% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0;
  }
}

.upload-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin-bottom: 8px;
}

.upload-format {
  font-size: 12px;
  color: #94a3b8;
}

.upload-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  border-radius: 8px;
}

/* Template Area */
.template-area {
  height: 280px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8fafc;
}

.template-area:hover {
  border-color: #0d2c6d;
  background-color: #f1f5f9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.template-icon-container {
  margin-bottom: 16px;
}

.template-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.template-text {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin-bottom: 16px;
}

.template-preview {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.template-header {
  display: flex;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.template-row {
  display: flex;
}

.template-cell {
  flex: 1;
  padding: 8px;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-right: 1px solid #e2e8f0;
}

.template-cell:last-child {
  border-right: none;
}

.template-header .template-cell {
  font-weight: 600;
  color: #0f172a;
}

.template-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  border-radius: 8px;
}

/* Selected File */
.selected-file {
  margin-top: 24px;
  padding: 12px 16px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.file-info {
  display: flex;
  align-items: center;
}

.file-details {
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
  color: #0f172a;
  margin-bottom: 2px;
}

.file-size {
  font-size: 12px;
  color: #64748b;
}

/* Results Step Styles */
.results-content {
  padding: 24px;
}

.results-summary {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-cards {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.result-card {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  transition: transform 0.2s ease;
}

.result-card:hover {
  transform: translateY(-2px);
}

.success-card {
  background-color: #f0fdf4;
  border: 1px solid #86efac;
}

.error-card {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
}

.result-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
}

.success-bg {
  background-color: #22c55e;
}

.error-bg {
  background-color: #ef4444;
}

.result-details {
  display: flex;
  flex-direction: column;
}

.result-count {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.result-label {
  font-size: 14px;
  color: #64748b;
}

/* Failed Items */
.failed-items-container {
  background-color: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 8px;
  overflow: hidden;
}

.failed-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background-color: #fff1f2;
  transition: background-color 0.2s ease;
}

.failed-items-header:hover {
  background-color: #ffe4e6;
}

.failed-items-title {
  font-weight: 600;
  color: #e11d48;
  display: flex;
  align-items: center;
}

.failed-items-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 8px 16px;
  background-color: #fff;
  border-top: 1px solid #fecdd3;
}

.failed-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.failed-item:last-child {
  border-bottom: none;
}

.failed-item-phone {
  font-weight: 500;
  color: #0f172a;
}

.failed-item-reason {
  color: #64748b;
  font-size: 14px;
}

/* Send Call Option */
.send-call-option {
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.checkbox-label {
  font-size: 16px;
  font-weight: 500;
  color: #0f172a;
}

/* Progress */
.progress-container {
  padding: 16px 24px;
  text-align: center;
}

.progress-text {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

/* Actions */
.import-actions {
  padding: 16px 24px 24px;
}

.import-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  border-radius: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .import-options {
    flex-direction: column;
  }

  .option-divider {
    flex-direction: row;
    padding: 16px 0;
  }

  .divider-line {
    width: 80px;
    height: 1px;
  }

  .divider-text {
    margin: 0 16px;
  }

  .upload-area,
  .template-area {
    height: 220px;
  }

  .result-cards {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .import-header {
    padding: 20px 16px 12px;
  }

  .import-title {
    font-size: 20px;
  }

  .import-content,
  .results-content {
    padding: 16px;
  }

  .upload-area,
  .template-area {
    height: 200px;
    padding: 16px;
  }

  .import-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }

  .import-btn,
  .v-btn {
    width: 100%;
  }
}
</style>